import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import "./MicModalComponent.scss";
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import commonService from "@/services/common.service";

interface MicModalComponentProps {
    isOpen: boolean;
    onClose: () => void;
    onTranscriptChange: (transcript: string) => void;
    projectId?: string;
}

const MicModalComponent: React.FC<MicModalComponentProps> = ({
    isOpen,
    onClose,
    onTranscriptChange,
    projectId
}) => {
    const [isRecording, setIsRecording] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Speech recognition hook
    const {
        transcript,
        resetTranscript,
        browserSupportsSpeechRecognition,
        listening
    } = useSpeechRecognition();

    // Check browser support
    useEffect(() => {
        if (!browserSupportsSpeechRecognition) {
            setError('Speech recognition is not supported in this browser.');
        }
    }, [browserSupportsSpeechRecognition]);

    const initializeAudio = useCallback(async () => {
        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                console.error('getUserMedia is not supported');
                return false;
            }
            
            await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: 44100
                } 
            });
            
            return true;
        } catch (error) {
            console.error('Error accessing microphone:', error);
            setError('Error accessing microphone. Please check permissions.');
            return false;
        }
    }, []);

    const startRecording = useCallback(async () => {
        if (isRecording) return;
        
        setError(null);
        const success = await initializeAudio();
        if (!success) return;
        
        setIsRecording(true);
        resetTranscript();
        
        // Start speech recognition with better configuration
        try {
            SpeechRecognition.startListening({ 
                continuous: true, 
                language: 'en-US',
                interimResults: true
            });
        } catch (error) {
            console.error('Error starting speech recognition:', error);
            setError('Failed to start speech recognition.');
            setIsRecording(false);
        }
    }, [isRecording, initializeAudio, resetTranscript]);


    // Auto-start recording when modal opens
    useEffect(() => {
        if (isOpen) {
            // Reset transcript when modal opens
            resetTranscript();
            
            if (!isRecording) {
                const timer = setTimeout(() => {
                    startRecording();
                }, 100);
                return () => clearTimeout(timer);
            }
        }
    }, [isOpen, isRecording, resetTranscript,startRecording]);   

  
    const stopRecording = useCallback(() => {
        if (!isRecording) return;
        
        setIsRecording(false);
        
        try {
            SpeechRecognition.stopListening();
        } catch (error) {
            console.error('Error stopping speech recognition:', error);
        }
    }, [isRecording]);

    const handleMicClick = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    const handleClose = () => {
        commonService.handleMixpanelTrack("Voice input modal closed", {
            project_id: projectId,
        });
        if (isRecording) {
            stopRecording();
        }
        onClose();
    };

    const handleSubmit = () => {
        commonService.handleMixpanelTrack("Voice input modal submitted", {
            project_id: projectId,
            transcript: transcript.trim(),
        });
        if (transcript.trim()) {
            onTranscriptChange(transcript);
        }
        onClose();
    };

    const handleClear = () => {
        resetTranscript();
        setError(null);
        commonService.handleMixpanelTrack("Voice input modal cleared");
        if (isRecording) {
            stopRecording();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-md w-full mx-4 p-0 border-0 shadow-2xl create-project-dialog-content">
                <div className="p-8">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <h2 className="text-2xl font-bold text-[var(--foreground)] mb-2">
                            Voice Input
                        </h2>
                    </div>

                    {/* Error message */}
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg"
                        >
                            <p className="text-sm text-red-400">
                                ⚠️ {error}
                            </p>
                        </motion.div>
                    )}

                    {/* Main mic button */}
                    <div className="flex justify-center mb-8">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleMicClick}
                            className={cn(
                                "relative w-24 h-24 rounded-full flex items-center justify-center transition-all duration-300",
                                isRecording 
                                    ? "bg-red-500 shadow-lg shadow-red-500/50" 
                                    : "bg-blue-500 hover:bg-blue-600 shadow-lg shadow-blue-500/50"
                            )}
                        >
                            <AnimatePresence mode="wait">
                                {isRecording ? (
                                    <motion.div
                                        key="mic-off"
                                        initial={{ scale: 0, rotate: -180 }}
                                        animate={{ scale: 1, rotate: 0 }}
                                        exit={{ scale: 0, rotate: 180 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <MicOff className="w-10 h-10 text-white" />
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="mic"
                                        initial={{ scale: 0, rotate: 180 }}
                                        animate={{ scale: 1, rotate: 0 }}
                                        exit={{ scale: 0, rotate: -180 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <Mic className="w-10 h-10 text-white" />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.button>
                    </div>

                    {/* Real-time transcript display */}
                    {isRecording && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mb-6"
                        >
                            <div className="text-center mb-4">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <Volume2 className="w-4 h-4 text-blue-500" />
                                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                        {listening ? "Listening..." : "Processing..."}
                                    </span>
                                </div>
                                
                                {/* Transcript display */}
                                {transcript && (
                                    <div className="mb-4 p-4 bg-[var(--sidebar-background)] border border-[rgba(15,251,149,0.13)] rounded-lg">
                                        <div className="text-sm text-[var(--foreground)] leading-relaxed">
                                            <strong>Your Voice Input:</strong><br />
                                            {transcript}
                                        </div>
                                    </div>
                                )}

                                {/* Listening animation - same as requirement gathering */}
                                <div className="voice-status-indicator__mic-waves">
                                    <div className="wave"></div>
                                    <div className="wave"></div>
                                    <div className="wave"></div>
                                    <div className="wave"></div>
                                    <div className="wave"></div>
                                    <div className="wave"></div>
                                    <div className="wave"></div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* Action buttons */}
                    <div className="flex gap-3">
                        <Button
                            onClick={handleClose}
                            className="flex-1 border border-[rgba(15,251,149,0.13)] bg-transparent hover:bg-[var(--sidebar-background)] text-[var(--foreground)]"
                        >
                            Cancel
                        </Button>
                        {transcript.trim() &&(
                            <>
                                <Button
                                    onClick={handleClear}
                                    className="flex-1 border border-[rgba(15,251,149,0.13)] bg-transparent hover:bg-[var(--sidebar-background)] text-[var(--foreground)]"
                                >
                                    Clear
                                </Button>
                                <Button
                                    onClick={handleSubmit}
                                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                                >
                                    Use This Text
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default MicModalComponent;
